<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('super-organizations')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Edit Organization</h1>
        </header>

        <section class="module__body">
            <form @submit.prevent="updateOrganization">
                <div class="form-group">
                    <label for="name">Name</label>
                    <input
                        id="name"
                        v-model="name"
                        type="text"
                        required
                        maxlength="32"
                    />
                </div>

                <div class="form-group organization-type">
                    <label for="organization_type">Organization Type</label>
                    <select
                        id="organization-type"
                        v-model="organizationType"
                        data-test-id="organization-type"
                        required
                    >
                        <option
                            disabled
                            selected
                            value=""
                        >
                            Please Choose Type
                        </option>
                        <option
                            v-for="type in organizationTypes"
                            :key="type"
                            :value="type"
                        >
                            {{ type }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="admin_emails">
                        Admins
                        <span class="tip">(List of emails, each separated by new line)</span>
                    </label>
                    <textarea
                        id="admin_emails"
                        v-model="admin_emails"
                        required
                    />
                </div>

                <fieldset>
                    <legend>Limits</legend>
                    <div class="form-group">
                        <label for="frequency_set_limit">Frequency Set Limit</label>
                        <input
                            id="frequency_set_limit"
                            v-model="frequency_set_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="field_user_limit">User Limit</label>
                        <input
                            id="field_user_limit"
                            v-model="field_user_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="mission_control_user_limit">Map User Limit</label>
                        <input
                            id="mission_control_user_limit"
                            v-model="mission_control_user_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="pli_limit">PLI Limit</label>
                        <input
                            id="pli_limit"
                            v-model="pli_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="mapbox_tile_limit">Mapbox Tile Limit</label>
                        <input
                            id="mapbox_tile_limit"
                            v-model="mapbox_tile_limit"
                            type="number"
                            required
                        />
                    </div>
                </fieldset>

                <fieldset>
                    <legend>Portal Permissions</legend>
                    <feature-toggle-field
                        :availableFeatures="availableOrganizationFeatures"
                        :selectedFeatures="selectedOrganizationFeatures"
                        @toggle="organizationFeatureToggled"
                    ></feature-toggle-field>
                </fieldset>

                <fieldset>
                    <legend>Mobile Permissions + Limits</legend>
                    <feature-toggle-field
                        :availableFeatures="availableMobileFeatures"
                        :selectedFeatures="selectedMobileFeatures"
                        :userCounts="mobile_feature_user_counts"
                        :showUserCounts="true"
                        showLimits
                        @toggle="mobileFeatureToggled"
                        @limit-update="mobileFeatureLimitUpdated"
                    ></feature-toggle-field>
                </fieldset>

                <fieldset>
                    <legend>Salesforce</legend>
                    <orders-field
                        :orders="orders"
                        @add-order="orders.push($event)"
                        @remove-order="orders.splice($event, 1)"
                    ></orders-field>
                </fieldset>

                <fieldset>
                    <legend>Billing Cycle</legend>
                    <billing-cycle-field
                        :availableFeatures="availablePremiumMobileFeatures"
                        :selectedFeatures="selectedMobileFeatures"
                        :billingIsRecurring="billing_is_recurring"
                        :billingStartedAt="billing_started_at"
                        :billingRenewsOn="billing_renews_on"
                        :licenseOverageIsVisible="licenseOverageIsVisible"
                        :licenseOverageAmount="license_overage_amount"
                        :licenseOverageType="license_overage_type"
                        :licenseOverageIsLocked="licenseOverageIsLocked && !licenseOverageIsVisible"
                        :mobileFeatureUserCounts="mobile_feature_user_counts"
                        @toggle="mobileFeatureToggled"
                        @limit-update="mobileFeatureLimitUpdated"
                        @recurring-update="billing_renews_on = (billing_is_recurring = $event) ? billing_renews_on : ''"
                        @start-date-update="billing_started_at = $event"
                        @renewal-date-update="billing_renews_on = $event"
                        @license-overage-is-visible-update="licenseOverageIsVisible = $event"
                        @license-overage-amount-update="license_overage_amount = $event"
                        @license-overage-type-update="license_overage_type = $event"
                    />
                </fieldset>

                <fieldset>
                    <legend>License Purchase History</legend>
                    <license-purchases-field
                        :purchases="licenses"
                        :usedCount="used_license_count"
                        :licenseOverageIsVisible="licenseOverageIsVisible"
                        @license-purchase-change="licensePurchasesChanged"
                    ></license-purchases-field>
                </fieldset>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                    >
                        <div>Update</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import axios from 'axios'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import FeatureToggleField from '@/components/FeatureToggleField.vue'
import OrdersField from '@/components/OrdersField.vue'
import BillingCycleField from '@/components/BillingCycleField.vue'
import LicensePurchasesField from '@/components/LicensePurchasesField.vue'
import organizationTypes from '@/constants/organization-types'

export default {
    components: {
        FormErrors,
        FeatureToggleField,
        OrdersField,
        BillingCycleField,
        LicensePurchasesField,
    },

    mixins: [canToast, hasBackButton],

    data() {
        return {
            id: this.$route.params.id,
            name: null,
            admin_emails: null,
            frequency_set_limit: 25,
            field_user_limit: 100,
            pli_limit: 5,
            mapbox_tile_limit: 20000,
            mission_control_user_limit: 1,
            errorResponse: null,
            submitted: false,
            orders: [],
            selectedOrganizationFeatures: {},
            availableOrganizationFeatures: [],
            selectedMobileFeatures: {},
            availableMobileFeatures: [],
            availablePremiumMobileFeatures: [],
            licenses: [],
            billing_is_recurring: false,
            billing_started_at: '',
            billing_renews_on: '',
            licenseOverageIsVisible: false,
            license_overage_amount: 20,
            license_overage_type: 'PERCENTAGE',
            used_license_count: 0,
            purchased_license_count: 0,
            mobile_feature_user_counts: [],
            organization_type: null,
            organizationTypes,
            selectedOrgFeaturesAtLoad: [],
        }
    },

    computed: {
        licenseOverageIsLocked() {
            return !this.licenses.some(({ count }) => count !== null && count > 0)
        },

        organizationType: {
            get() {
                return this.organization_type === null ? '' : this.organization_type
            },

            set(value) {
                this.organization_type = Object.values(organizationTypes).includes(value) ? value : null
            },
        },
    },

    mounted() {
        const requests = [
            this.$http.get(`organization-features/${this.id}`),
            this.$http.get(`organizations/${this.id}`),
            this.$http.get('mobile-features/all'),
        ]

        this.$http.all(requests).then(
            axios.spread((...responses) => {
                const organizationFeatures = responses[0].data
                const organization = responses[1].data
                const mobileFeatures = responses[2].data

                this.selectedOrgFeaturesAtLoad = organization.features
                this.name = organization.name
                this.frequency_set_limit = organization.frequency_set_limit
                this.field_user_limit = organization.field_user_limit
                this.pli_limit = organization.pli_limit
                this.mapbox_tile_limit = organization.mapbox_tile_limit
                this.mission_control_user_limit = organization.mission_control_user_limit
                this.admin_emails = organization.admins.reduceRight((adminEmails, admin) => {
                    return `${admin.email}\n${adminEmails}`
                }, '')
                this.orders = organization.orders
                this.availableOrganizationFeatures = organizationFeatures
                this.selectedOrganizationFeatures = this.buildSelectedFeatures(organization.features)
                this.availableMobileFeatures = mobileFeatures.filter((feature) => !feature.is_premium)
                this.availablePremiumMobileFeatures = mobileFeatures.filter((feature) => feature.is_premium)
                this.selectedMobileFeatures = this.buildSelectedFeatures(organization.mobile_features)
                this.licenses = organization.licenses
                this.billing_is_recurring = Boolean(organization.billing_is_recurring)
                this.billing_started_at = organization.billing_started_at
                this.billing_renews_on = organization.billing_renews_on
                this.used_license_count = organization.used_license_count
                this.purchased_license_count = organization.purchased_license_count
                this.mobile_feature_user_counts = organization.mobile_feature_user_counts
                this.organization_type = organization.type

                // if any overage information is missing, then the data is incorrect, hide and set defaults
                if (!organization.license_overage_amount || !organization.license_overage_type) {
                    this.license_overage_amount = 20
                    this.license_overage_type = 'PERCENTAGE'
                    this.licenseOverageIsVisible = false
                } else {
                    // else set the proper values and show
                    this.license_overage_amount = organization.license_overage_amount
                    this.license_overage_type = organization.license_overage_type
                    this.licenseOverageIsVisible = true
                }
            })
        )
    },

    methods: {
        buildSelectedFeatures(selectedFeatures) {
            const result = {}

            selectedFeatures.forEach((feature) => {
                const { id } = feature
                const limit = feature?.pivot?.user_limit ?? null

                result[id] = {
                    id,
                    limit,
                }
            })

            return result
        },

        organizationFeatureToggled(feature) {
            if (this.selectedOrganizationFeatures[feature.id]) {
                delete this.selectedOrganizationFeatures[feature.id]
            } else {
                this.selectedOrganizationFeatures[feature.id] = {
                    id: feature.id,
                    limit: null,
                }
            }
        },

        mobileFeatureToggled(feature) {
            if (this.selectedMobileFeatures[feature.id]) {
                delete this.selectedMobileFeatures[feature.id]
            } else {
                this.selectedMobileFeatures[feature.id] = {
                    id: feature.id,
                    limit: null,
                }
            }
        },

        mobileFeatureLimitUpdated(feature, limit) {
            if (this.selectedMobileFeatures[feature.id]) {
                this.selectedMobileFeatures[feature.id].limit = limit
            }
        },

        licensePurchasesChanged(licenses) {
            this.licenses = licenses
        },

        updateOrganization() {
            this.submitted = true

            const features = Object.values(this.selectedOrganizationFeatures).map((selectedFeature) => {
                const result = this.availableOrganizationFeatures.find((feature) => feature.id === selectedFeature.id)
                if (!result) {
                    // persist org feature from load if it was hidden
                    return this.selectedOrgFeaturesAtLoad.find((feature) => feature.id === selectedFeature.id)
                }

                return result
            })

            const mobileFeatures = Object.values(this.selectedMobileFeatures)

            let licenseOverageAmount = this.license_overage_amount
            let licenseOverageType = this.license_overage_type

            // if overage section is not visible, or the value is zero, set
            // overage info to null
            if (!this.licenseOverageIsVisible || licenseOverageAmount === 0) {
                licenseOverageAmount = null
                licenseOverageType = null
            }

            const data = {
                name: this.name,
                admin_emails: this.admin_emails,
                frequency_set_limit: this.frequency_set_limit,
                field_user_limit: this.field_user_limit,
                pli_limit: this.pli_limit,
                mapbox_tile_limit: this.mapbox_tile_limit,
                mission_control_user_limit: this.mission_control_user_limit,
                orders: this.orders,
                mobile_features: mobileFeatures,
                features,
                licenses: this.licenses,
                billing_is_recurring: Number(this.billing_is_recurring),
                billing_started_at: this.billing_started_at,
                billing_renews_on: this.billing_renews_on,
                license_overage_amount: licenseOverageAmount,
                license_overage_type: licenseOverageType,
                type: this.organization_type,
            }

            this.$http
                .put(`organizations/${this.id}`, data)
                .then(() => {
                    this.toast('Organization successfully updated!')
                    this.$router.push('/organizations')
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
    },
}
</script>
